import React from 'react';

import { Interactive } from './InteractiveElement';
import { Text } from './Text';

import {
	useComponentDefaultProps,
	useMove,
	UseMovePosition,
	useUncontrolled,
} from 'domain/hooks';
import { Key } from './Keyboard';
import { cn } from 'domain/core';
import { DivProps, InputProps, SpanProps } from './common-types';
import {
	clamp,
	getPosition,
	getSignificantDecimals,
	mergeRefs,
} from 'domain/utils';
import { Col } from './Flex';

/**
 *
 *
 * CONSIDERAR LA MIGRACIÓN A
 *  https://slider-react-component.vercel.app/
 *  o
 *  https://react-compound-slider.netlify.app/
 *
 *
 */

type SliderDirection = 'horizontal' | 'vertical';
type SliderMark = {
	value: number;
	label: string;
};
interface GetChangeValue {
	value: number;
	containerWidth?: number;
	min: number;
	max: number;
	step: number;
	precision?: number;
}
export function getChangeValue({
	value,
	containerWidth,
	min,
	max,
	step,
	precision,
}: GetChangeValue) {
	const left = !containerWidth
		? value
		: Math.min(Math.max(value, 0), containerWidth) / containerWidth;
	const dx = left * (max - min);
	let nextValue = (dx !== 0 ? (dx / step) * step : 0) + min;
	const decimals = Number.isInteger(step) ? 0 : getSignificantDecimals(step);
	nextValue = Number(nextValue.toFixed(decimals));
	const nextValueWithinStep = Math.max(nextValue, min);

	if (precision !== undefined) {
		return Number(nextValueWithinStep.toFixed(precision));
	}

	return nextValueWithinStep;
}

export function getClientPosition(event: any) {
	if ('TouchEvent' in window && event instanceof window.TouchEvent) {
		const touch = event.touches[0];
		return [touch.clientX, touch.clientY];
	}
	return [event.clientX, event.clientY];
}

export interface SliderProps
	extends Omit<
		InputProps,
		'onChange' | 'value' | 'defaultValue' | 'type' | 'min' | 'max' | 'step'
	> {
	onChange?: (value: number) => void;
	onChangeComplete?: (value: number) => void;
	formatLabelValue?: (value: number) => string | number;
	value?: number;
	defaultValue?: number;
	barSize?: number;
	precision?: number;
	direction?: SliderDirection;
	min?: number;
	max?: number;
	step?: number;
	marks?: SliderMark[];
	classNames?: {
		Thumb?: string;
		Bar?: string;
		ThumbLabel?: string;
		Progress?: string;
		Marks?: string;
	};
	components?: {
		Thumb?: SliderTypes.Thumb;
		Bar?: SliderTypes.Bar;
		ThumbLabel?: SliderTypes.ThumbLabel;
		Progress?: SliderTypes.Progress;
		Marks?: any;
	};
}

const defaultProps: SliderProps = {
	barSize: 8,
	defaultValue: 0,
	max: 100,
	min: 0,
	step: 1,
	precision: 1,
	direction: 'horizontal',
	formatLabelValue: (val) => val,
	components: {},
	classNames: {
		Bar: '',
		Progress: '',
		Thumb: '',
		ThumbLabel: '',
		Marks: '',
	},
};

const Root = React.forwardRef((props: SliderProps, ref: any) => {
	const _props = useComponentDefaultProps(defaultProps, props);

	const [value, handleOnChange] = useUncontrolled<number>({
		defaultValue: _props.defaultValue!,
		value: _props.value,
		onChange: _props.onChange,
		finalValue: clamp(0, _props.min!, _props.max!),
	});

	const valueRef = React.useRef<number>(value);
	const thumb = React.useRef<HTMLDivElement>(null);

	const position = React.useMemo(
		() => getPosition({ value, max: _props.max!, min: _props.min! }),
		[value, _props.max, _props.min]
	);

	const handlePositionChange = React.useCallback(
		({ x, y }: UseMovePosition) => {
			if (!_props.disabled) {
				const _value = _props.direction === 'horizontal' ? x : 1 - y;
				const nextValue = getChangeValue({
					max: _props.max!,
					min: _props.min!,
					step: _props.step!,
					precision: _props.precision,
					value: _value,
				});
				handleOnChange(nextValue);
				valueRef.current = nextValue;
			}
		},
		[
			handleOnChange,
			_props.max,
			_props.min,
			_props.step,
			_props.disabled,
			_props.precision,
			_props.direction,
		]
	);

	const onScrubEnd = React.useCallback(() => {
		_props.onChangeComplete?.(valueRef.current);
	}, [_props.onChangeComplete]);

	const { ref: barRef, active } = useMove(handlePositionChange, {
		onScrubEnd: onScrubEnd,
	});

	const handleThumbMouseDown = React.useCallback(
		(evt: React.MouseEvent<HTMLDivElement>) => evt.stopPropagation(),
		[]
	);

	const handleBarKeyDown: React.KeyboardEventHandler<HTMLDivElement> =
		React.useCallback(
			(evt) => {
				if (!_props.disabled) {
					switch (evt.key as Key) {
						case Key.ArrowDown:
						case Key.ArrowLeft: {
							evt.stopPropagation();
							thumb.current?.focus();
							const nextValue = clamp(
								value - _props.step!,
								_props.min!,
								_props.max!
							);
							handleOnChange(nextValue);
							_props.onChangeComplete?.(nextValue);
							break;
						}
						case Key.ArrowUp:
						case Key.ArrowRight: {
							evt.stopPropagation();
							thumb.current?.focus();
							const nextValue = clamp(
								value + _props.step!,
								_props.min!,
								_props.max!
							);
							handleOnChange(nextValue);
							_props.onChangeComplete?.(nextValue);
							break;
						}
						case Key.Home: {
							evt.stopPropagation();
							thumb.current?.focus();
							const nextValue = _props.min!;
							handleOnChange(nextValue);
							_props.onChangeComplete?.(nextValue);
							break;
						}
						case Key.End: {
							evt.stopPropagation();
							thumb.current?.focus();
							const nextValue = _props.max!;
							handleOnChange(nextValue);
							_props.onChangeComplete?.(nextValue);
							break;
						}
					}
				}
			},
			[
				_props.disabled,
				_props.step,
				_props.min,
				_props.max,
				value,
				_props.onChangeComplete,
				handleOnChange,
			]
		);

	const handleBarFocus = React.useCallback(() => thumb.current?.focus(), []);

	const { _Thumb, _ThumbLabel, _Bar, _Progress, _Marks } = React.useMemo(
		() => ({
			_Thumb: _props.components!.Thumb ?? Thumb,
			_ThumbLabel: _props.components!.ThumbLabel ?? ThumbLabel,
			_Bar: _props.components!.Bar ?? Bar,
			_Progress: _props.components!.Progress ?? Progress,
			_Marks: _props.components!.Marks ?? Marks,
		}),
		[_props.components]
	);

	return (
		<Interactive onKeyDown={handleBarKeyDown}>
			<_Bar
				direction={_props.direction!}
				barSize={_props.barSize!}
				className={_props.classNames!.Bar}
				onFocus={handleBarFocus}
				ref={mergeRefs(barRef, ref)}>
				<input type='hidden' value={value} />
				<_Progress
					barSize={_props.barSize!}
					position={position}
					direction={_props.direction!}
					className={_props.classNames!.Progress}
				/>
				<_Marks
					marks={_props.marks}
					barSize={_props.barSize!}
					direction={_props.direction!}
					max={_props.max!}
					min={_props.min!}
					className={_props.classNames?.Marks}
				/>
				<_Thumb
					barSize={_props.barSize!}
					active={active}
					position={position}
					className={_props.classNames!.Thumb}
					onMouseDown={handleThumbMouseDown}
					ref={thumb}
					direction={_props.direction!}>
					<_ThumbLabel
						direction={_props.direction!}
						position={position}
						active={active}
						className={_props.classNames!.ThumbLabel}>
						{_props.formatLabelValue!(value)}
					</_ThumbLabel>
				</_Thumb>
			</_Bar>
		</Interactive>
	);
});

interface BarProps extends DivProps {
	barSize: number;
	direction: SliderDirection;
}
type BarType = React.ForwardRefExoticComponent<
	BarProps & React.RefAttributes<HTMLDivElement>
>;
const Bar: BarType = React.forwardRef(
	({ className, barSize, direction, ...props }, ref) => {
		return (
			<div
				{...props}
				className={cn('relative w-full rounded-full bg-neutral-300', className)}
				style={
					direction === 'horizontal' ? { height: barSize } : { width: barSize }
				}
				ref={ref}
			/>
		);
	}
);

interface ThumbProps extends DivProps {
	position: number;
	barSize: number;
	active: boolean;
	direction: SliderDirection;
}
type ThumbType = React.ForwardRefExoticComponent<
	ThumbProps & React.RefAttributes<HTMLDivElement>
>;
const Thumb: ThumbType = React.forwardRef(
	({ className, position, barSize, active, direction, ...props }, ref: any) => {
		const positionStyles =
			direction === 'horizontal'
				? {
						left: `${position.toFixed(2)}%`,
						transform: `translateY(-${barSize / 2}px)translateX(-4px)`,
				  }
				: {
						top: `${(100 - position).toFixed(2)}%`,
						transform: `translateY(-4px)translateX(-${barSize / 2}px)`,
				  };

		return (
			<Interactive>
				<div
					className={cn(
						'absolute',
						'h-4 w-4 rounded-full bg-primary-200',
						'transition-color transition-transform',
						active && 'scale-125 bg-primary-400',
						'focus:ring-1 focus:ring-primary-300',
						className
					)}
					style={positionStyles}
					{...props}
					ref={ref}
				/>
			</Interactive>
		);
	}
);

interface ProgressProps extends DivProps {
	position: number;
	barSize: number;
	direction: SliderDirection;
}
type ProgressType = React.ForwardRefExoticComponent<
	ProgressProps & React.RefAttributes<HTMLDivElement>
>;
const Progress: ProgressType = React.forwardRef(
	({ className, position, barSize, direction, ...props }, ref) => {
		return (
			<div
				className={cn('absolute rounded-full bg-primary-300', className)}
				{...props}
				ref={ref}
				style={
					direction === 'horizontal'
						? {
								height: barSize,
								width: `${position}%`,
								left: 0,
						  }
						: {
								height: `${position}%`,
								width: barSize,
								left: 0,
								bottom: `0`,
						  }
				}
			/>
		);
	}
);

interface ThumbLabelProps extends SpanProps {
	position: number;
	direction: SliderDirection;
	active: boolean;
}
type ThumbLabelType = React.ForwardRefExoticComponent<
	ThumbLabelProps & React.RefAttributes<HTMLSpanElement>
>;
const ThumbLabel: ThumbLabelType = React.forwardRef(
	({ className, position, direction, active, ...props }, ref) => {
		const positionStyles =
			direction === 'horizontal'
				? {
						top: 16,
				  }
				: {
						left: 20,
						top: -4,
				  };

		return (
			<Text
				className={cn(
					'pointer-events-none relative select-none text-sm focus:outline-none',
					'transition-colors',
					active && 'text-primary-300',
					className
				)}
				style={positionStyles}
				{...props}
				ref={ref}
			/>
		);
	}
);

interface MarksProps extends DivProps {
	marks?: SliderMark[];
	direction: SliderDirection;
	max: number;
	min: number;
	barSize: number;
	markLabelClassName?: string;
	markDotClassName?: string;
	markClassName?: string;
}
type MarksType = React.ForwardRefExoticComponent<
	MarksProps & React.RefAttributes<HTMLDivElement>
>;
const Marks: MarksType = React.forwardRef((props, ref) => {
	if (!props.marks || props.marks.length === 0) {
		return null;
	}

	return (
		<div
			className={cn('absolute', 'top-0 left-0 h-full w-full', props.className)}>
			{props.marks.map((mark, i) => {
				const position = getPosition({
					value: mark.value,
					max: props.max,
					min: props.min,
				});

				const last = i === (props.marks?.length ?? 0) - 1;

				return (
					<Col
						key={mark.label}
						className={cn(
							'absolute h-auto w-auto',
							'flex-nowrap items-center justify-center gap-0 overflow-visible',
							props.markClassName
						)}
						style={{
							height: `${props.barSize / 2}px`,
							width: `${props.barSize / 2}px`,
							...(props.direction === 'horizontal'
								? {
										left: last ? `98%` : `${position.toFixed(2)}%`,
										transform: `translateY(${
											props.barSize / 4
										}px)translateX(-0px)`,
								  }
								: {
										top: last ? '2%' : `${(100 - position).toFixed(2)}%`,
										transform: `translateY(-0px)translateX(${
											props.barSize / 4
										}px)`,
								  }),
						}}>
						<div
							className={cn(
								'absolute rounded-full bg-neutral-50',
								props.markDotClassName
							)}
							style={{
								height: `${props.barSize / 2}px`,
								width: `${props.barSize / 2}px`,
							}}
						/>
						<Text
							className={cn(
								'relative',
								'select-none text-center text-[0.7em] leading-3',
								props.direction === 'horizontal'
									? '-top-5'
									: '-left-6 -top-[5px]',
								props.markLabelClassName
							)}>
							{mark.label}
						</Text>
					</Col>
				);
			})}
		</div>
	);
});

export const Slider = {
	Root,
	Thumb,
	ThumbLabel,
	Bar,
	Progress,
	Marks,
};

export namespace SliderTypes {
	export type Thumb = ThumbType;
	export type ThumbLabel = ThumbLabelType;
	export type Bar = BarType;
	export type Progress = ProgressType;
	export type Direction = SliderDirection;
	export type Mark = SliderMark;
	export type Marks = MarksType;
}
